import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  deleteExamVersion,
  mergeExamVersions,
  updateExamVersionCode,
} from "../../../components/Api/ExamGradingEndpoints";

function normalizeVersion(value, fallback = "1") {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
}

function compareVersions(a, b) {
  const aNum = Number.parseInt(a, 10);
  const bNum = Number.parseInt(b, 10);
  const aIsNum = Number.isInteger(aNum) && String(aNum) === String(a);
  const bIsNum = Number.isInteger(bNum) && String(bNum) === String(b);
  if (aIsNum && bIsNum) return aNum - bNum;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return String(a).localeCompare(String(b));
}

function getEffectiveAnswers(item) {
  if (!item || String(item?.omit || "N").toUpperCase() === "Y") return [];

  const modifiedAnswers = Array.isArray(item?.modified_answer)
    ? item.modified_answer
        .map((answer) =>
          String(answer || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean)
    : [];
  if (modifiedAnswers.length > 0) return modifiedAnswers;

  return Array.isArray(item?.original_answer)
    ? item.original_answer
        .map((answer) =>
          String(answer || "")
            .trim()
            .toUpperCase(),
        )
        .filter(Boolean)
    : [];
}

function buildAnswerKeyString(key, header) {
  if (!key) return "";
  const lq = Number.parseInt(header?.lqnum, 10) || 1;
  const hq = Number.parseInt(header?.hqnum, 10) || lq;
  const count = Math.max(0, hq - lq + 1);
  const byQ = new Map();

  for (const item of key?.items || []) {
    byQ.set(Number(item?.item_no), item);
  }

  const parts = [];
  for (let i = 0; i < count; i++) {
    const q = lq + i;
    const item = byQ.get(q);
    const answers = getEffectiveAnswers(item);
    if (!item || answers.length === 0) {
      parts.push(" ");
      continue;
    }

    parts.push(answers.join(","));
  }

  return parts.join("|");
}

function previewResponse(response) {
  const text = String(response || "").trim();
  if (text.length <= 48) return text;
  return `${text.slice(0, 48)}...`;
}

export default function VersionManagementDialog({
  open,
  setOpen,
  examProcessingState,
  token,
  reqnum,
  fetchRequestDetails,
  setSnackbar,
}) {
  const [draftByVersion, setDraftByVersion] = useState({});
  const [loadingByVersion, setLoadingByVersion] = useState({});
  const [mergeLoading, setMergeLoading] = useState(false);

  const rows = useMemo(() => {
    const keys = examProcessingState?.answer_keys || [];
    const students = examProcessingState?.students || [];
    const header = examProcessingState?.header || {};

    const keysByVersion = new Map();
    for (const key of keys) {
      const version = normalizeVersion(key?.version, "1");
      const arr = keysByVersion.get(version) || [];
      arr.push(key);
      keysByVersion.set(version, arr);
    }

    const studentsByVersion = new Map();
    for (const student of students) {
      const version = normalizeVersion(student?.version, "1");
      const arr = studentsByVersion.get(version) || [];
      arr.push(student);
      studentsByVersion.set(version, arr);
    }

    const allVersions = new Set([
      ...keysByVersion.keys(),
      ...studentsByVersion.keys(),
    ]);

    return [...allVersions]
      .map((version) => {
        const versionKeys = keysByVersion.get(version) || [];
        const versionStudents = (studentsByVersion.get(version) || [])
          .slice()
          .sort((a, b) => {
            return String(a?.sheet_sequence || "").localeCompare(
              String(b?.sheet_sequence || ""),
            );
          });

        return {
          version,
          keyCount: versionKeys.length,
          studentCount: versionStudents.length,
          answerKeyString: buildAnswerKeyString(versionKeys[0], header),
          students: versionStudents,
        };
      })
      .sort((a, b) => compareVersions(a.version, b.version));
  }, [examProcessingState]);

  const existingVersionSet = useMemo(() => {
    return new Set(rows.map((r) => normalizeVersion(r.version, "1")));
  }, [rows]);

  const mergePreview = useMemo(() => {
    const keys = examProcessingState?.answer_keys || [];
    const header = examProcessingState?.header || {};
    const lq = Number.parseInt(header?.lqnum, 10);
    const hq = Number.parseInt(header?.hqnum, 10);
    const questionCount =
      Number.isFinite(lq) && Number.isFinite(hq) && hq >= lq ? hq - lq + 1 : 0;

    if (rows.length < 2) {
      return {
        canMerge: false,
        questionCount,
        activeItemTotal: 0,
        reason:
          "At least two loaded versions are required before a merge is available.",
      };
    }

    if (!questionCount) {
      return {
        canMerge: false,
        questionCount: 0,
        activeItemTotal: 0,
        reason:
          "The request question range is invalid, so merge validation cannot run.",
      };
    }

    const versionsByItem = new Map();
    let activeItemTotal = 0;

    for (const key of keys) {
      const version = normalizeVersion(key?.version, "1");
      const itemsByNo = new Map();
      for (const item of key?.items || []) {
        itemsByNo.set(Number(item?.item_no), item);
      }

      for (let itemNo = lq; itemNo <= hq; itemNo += 1) {
        const item = itemsByNo.get(itemNo);
        if (!item || getEffectiveAnswers(item).length === 0) continue;

        activeItemTotal += 1;
        const versions = versionsByItem.get(itemNo) || [];
        versions.push(version);
        versionsByItem.set(itemNo, versions);
      }
    }

    const conflicts = [...versionsByItem.entries()]
      .filter(([, versions]) => versions.length > 1)
      .map(([itemNo, versions]) => ({
        itemNo,
        versions: [...new Set(versions)].sort(compareVersions),
      }))
      .sort((a, b) => a.itemNo - b.itemNo);

    if (conflicts.length > 0) {
      const sample = conflicts
        .slice(0, 6)
        .map((conflict) => conflict.itemNo)
        .join(", ");
      return {
        canMerge: false,
        questionCount,
        activeItemTotal,
        conflicts,
        reason: `Cannot merge because question(s) ${sample}${conflicts.length > 6 ? ", ..." : ""} have active answers in more than one version.`,
      };
    }

    if (activeItemTotal > questionCount) {
      return {
        canMerge: false,
        questionCount,
        activeItemTotal,
        reason: `Cannot merge because ${activeItemTotal} answered items exceed the ${questionCount}-question range.`,
      };
    }

    if (activeItemTotal === 0) {
      return {
        canMerge: false,
        questionCount,
        activeItemTotal,
        reason: "No answer key content was found to merge.",
      };
    }

    return {
      canMerge: true,
      questionCount,
      activeItemTotal,
      reason: `Ready to merge ${rows.length} versions into single-version code 1. ${activeItemTotal} answered items fit within ${questionCount} questions.`,
    };
  }, [examProcessingState, rows]);

  useEffect(() => {
    if (!open) return;
    const nextDraft = {};
    for (const row of rows) {
      nextDraft[row.version] = row.version;
    }
    setDraftByVersion(nextDraft);
  }, [open, rows]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const setLoading = useCallback((version, isLoading) => {
    setLoadingByVersion((prev) => ({
      ...prev,
      [version]: isLoading,
    }));
  }, []);

  const getErrorMessage = (error) => {
    return (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Unknown error"
    );
  };

  const dialogBusy =
    mergeLoading || Object.values(loadingByVersion).some(Boolean);

  const handleSaveVersion = useCallback(
    async (row) => {
      const oldVersion = normalizeVersion(row.version, "1");
      const nextRaw = draftByVersion[row.version];
      const newVersion = normalizeVersion(nextRaw, oldVersion);

      if (!newVersion) {
        setSnackbar?.({
          open: true,
          message: "Special code cannot be empty.",
          severity: "error",
        });
        return;
      }

      if (newVersion.length > 6) {
        setSnackbar?.({
          open: true,
          message: "Special code cannot exceed 6 characters.",
          severity: "error",
        });
        return;
      }

      if (newVersion === oldVersion) {
        setSnackbar?.({
          open: true,
          message: `Version ${oldVersion} is unchanged.`,
          severity: "info",
        });
        return;
      }

      if (existingVersionSet.has(newVersion) && newVersion !== oldVersion) {
        setSnackbar?.({
          open: true,
          message: `Version ${newVersion} already exists. Choose a unique special code.`,
          severity: "error",
        });
        return;
      }

      try {
        setLoading(oldVersion, true);
        await updateExamVersionCode(token, reqnum, oldVersion, newVersion);
        await fetchRequestDetails();
        setSnackbar?.({
          open: true,
          message: `Updated special code ${oldVersion} -> ${newVersion}.`,
          severity: "success",
        });
      } catch (error) {
        setSnackbar?.({
          open: true,
          message: `Failed to update special code: ${getErrorMessage(error)}`,
          severity: "error",
        });
      } finally {
        setLoading(oldVersion, false);
      }
    },
    [
      draftByVersion,
      existingVersionSet,
      fetchRequestDetails,
      reqnum,
      setLoading,
      setSnackbar,
      token,
    ],
  );

  const handleDeleteVersion = useCallback(
    async (row) => {
      const version = normalizeVersion(row.version, "1");
      const confirmed = window.confirm(
        `Delete version ${version}? This will remove its key and all student responses for that version.`,
      );
      if (!confirmed) return;

      try {
        setLoading(version, true);
        await deleteExamVersion(token, reqnum, version);
        await fetchRequestDetails();
        setSnackbar?.({
          open: true,
          message: `Deleted version ${version}.`,
          severity: "success",
        });
      } catch (error) {
        setSnackbar?.({
          open: true,
          message: `Failed to delete version: ${getErrorMessage(error)}`,
          severity: "error",
        });
      } finally {
        setLoading(version, false);
      }
    },
    [fetchRequestDetails, reqnum, setLoading, setSnackbar, token],
  );

  const handleMergeVersions = useCallback(async () => {
    const confirmed = window.confirm(
      "Merge all currently loaded versions into a single version 1? This rewrites the answer key, updates every student special code to 1, and rescored student rows will use the merged key.",
    );
    if (!confirmed) return;

    try {
      setMergeLoading(true);
      const result = await mergeExamVersions(token, reqnum);
      await fetchRequestDetails();
      setSnackbar?.({
        open: true,
        message: `Merged ${result?.source_versions?.length || rows.length} versions into single-version code 1.`,
        severity: "success",
      });
    } catch (error) {
      const conflicts = error?.response?.data?.conflicting_questions;
      const conflictSuffix =
        Array.isArray(conflicts) && conflicts.length > 0
          ? ` Overlapping questions: ${conflicts
              .slice(0, 6)
              .map((entry) => entry?.item_no)
              .filter((itemNo) => itemNo !== undefined)
              .join(", ")}${conflicts.length > 6 ? ", ..." : ""}.`
          : "";
      setSnackbar?.({
        open: true,
        message: `Failed to merge versions: ${getErrorMessage(error)}${conflictSuffix}`,
        severity: "error",
      });
    } finally {
      setMergeLoading(false);
    }
  }, [
    fetchRequestDetails,
    getErrorMessage,
    reqnum,
    rows.length,
    setSnackbar,
    token,
  ]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>Manage Exam Versions</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Common case: one file upload. Multi-upload cases can represent true
          multi-version scanning or separate single-version batches.
        </Alert>
        {rows.length > 1 && (
          <Alert
            severity={mergePreview.canMerge ? "success" : "warning"}
            sx={{ mb: 2 }}
          >
            <Box className="flex flex-col gap-3">
              <Box>
                <Typography variant="subtitle2">Edge-Case Merge</Typography>
                <Typography variant="body2">
                  Combine all loaded versions into a single version-1 answer key
                  when the keys slot into different question ranges without
                  overlapping.
                </Typography>
              </Box>
              <Typography variant="body2">{mergePreview.reason}</Typography>
              <Box>
                <Button
                  variant="contained"
                  disabled={!mergePreview.canMerge || dialogBusy}
                  startIcon={
                    mergeLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  onClick={handleMergeVersions}
                >
                  Merge All Versions Into One
                </Button>
              </Box>
            </Box>
          </Alert>
        )}
        {rows.length === 0 ? (
          <Alert severity="warning">
            No exam key/student version data is loaded for this request.
          </Alert>
        ) : (
          rows.map((row) => {
            const loading = !!loadingByVersion[row.version];
            return (
              <Accordion key={row.version} disableGutters sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box className="w-full flex items-center gap-3 flex-wrap">
                    <Chip
                      label={`Version ${row.version}`}
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label={`${row.keyCount} key${row.keyCount === 1 ? "" : "s"}`}
                      size="small"
                    />
                    <Chip
                      label={`${row.studentCount} student${row.studentCount === 1 ? "" : "s"}`}
                      size="small"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className="flex flex-col gap-3">
                    <Box className="flex items-center gap-2 flex-wrap">
                      <TextField
                        size="small"
                        label="Special Code"
                        value={draftByVersion[row.version] ?? row.version}
                        disabled={loading || mergeLoading}
                        onChange={(e) =>
                          setDraftByVersion((prev) => ({
                            ...prev,
                            [row.version]: e.target.value,
                          }))
                        }
                      />
                      <Button
                        variant="contained"
                        startIcon={
                          loading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        disabled={loading || mergeLoading}
                        onClick={() => handleSaveVersion(row)}
                      >
                        Update Code
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={loading || mergeLoading}
                        onClick={() => handleDeleteVersion(row)}
                      >
                        Delete Version
                      </Button>
                    </Box>

                    <TextField
                      label="Answer Key String"
                      value={
                        row.answerKeyString || "(No key data for this version)"
                      }
                      multiline
                      minRows={2}
                      maxRows={5}
                      InputProps={{
                        readOnly: true,
                        sx: { fontFamily: "monospace", fontSize: 12 },
                      }}
                    />

                    <Divider />
                    <Typography variant="subtitle2">
                      Student Responses for Version {row.version}
                    </Typography>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 260 }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Sheet</TableCell>
                            <TableCell>ID</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Score</TableCell>
                            <TableCell>Response Preview</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {row.students.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} align="center">
                                No students for this version.
                              </TableCell>
                            </TableRow>
                          ) : (
                            row.students.map((student) => (
                              <TableRow
                                key={`${row.version}-${student?.rsp_id || student?.sheet_sequence}`}
                              >
                                <TableCell>
                                  {student?.sheet_sequence || "-"}
                                </TableCell>
                                <TableCell>
                                  {student?.std_id_raw || "-"}
                                </TableCell>
                                <TableCell>
                                  {student?.std_name_raw || "-"}
                                </TableCell>
                                <TableCell>
                                  {student?.original_score ?? "-"}
                                </TableCell>
                                <TableCell
                                  sx={{ fontFamily: "monospace", fontSize: 12 }}
                                >
                                  {previewResponse(student?.std_response)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
